import React, { ReactNode } from 'react'
import {
  AssignmentTurnedIn,
  AttachMoney,
  BarChart,
  Dashboard,
  Description,
  Favorite,
  FileCopy,
  Home,
  List,
  LocalOffer,
  LocalShipping,
  MonetizationOn,
  People,
  PieChart,
  Summarize,
  SupervisedUserCircle,
} from '@mui/icons-material'
import { AssignmentInd, ManageAccounts, Warehouse } from '@mui/icons-material_modern'
import { GS_ROLE } from 'utils/constants'
import { SplitFeatureFlagName } from 'utils/featureFlagsDict'
import { AppTab } from 'AppRoutes'

interface AppTabRoute {
  icon: ReactNode
  title: string
  route: string
  matchPath: string
  treatmentKeys?: string[]
}

// when you change feature flags for routes specifically
// make sure to set the treatmentKey on the route that matches
// with the split io split
export const APP_TAB_ROUTES = (
  isReady: boolean,
  treatments: SplitIO.TreatmentsWithConfig,
  erpType?: string | undefined,
  rbacMap?: Record<string, boolean>,
): AppTabRoute[] => {
  const routes = [
    {
      icon: <LocalOffer />,
      title: 'Purchases',
      route: 'purchases',
      matchPath: '/app/purchases/:id*',
      treatmentKeys: ['app-route-purchases'],
    },
    {
      icon: <AssignmentTurnedIn />,
      title: 'Sales',
      route: 'sales',
      matchPath: '/app/sales/:id*',
      treatmentKeys: ['app-route-sales'],
    },
    {
      icon: <FileCopy />,
      title: 'Contracts',
      route: 'contracts',
      matchPath: '/app/contracts/:id*',
      treatmentKeys: ['app-route-contracts'],
    },
    {
      icon: <List />,
      title: 'Inventory',
      route: 'inventory',
      matchPath: '/app/inventory/:id*',
      treatmentKeys: ['app-route-inventory'],
    },
    {
      icon: <SupervisedUserCircle />,
      title: 'Customers',
      route: 'customers',
      matchPath: '/app/customers/:id*',
      treatmentKeys: ['app-route-customers'],
    },
    {
      icon: <PieChart />,
      title: 'Commodities',
      route: 'commodities',
      matchPath: '/app/commodities/:id*',
      treatmentKeys: ['app-route-commodities'],
    },
    {
      icon: <LocalShipping />,
      title: 'Freight',
      route: 'freight',
      matchPath: '/app/freight/:id*',
      treatmentKeys: ['app-route-freight'],
    },
    {
      icon: <AssignmentInd />,
      title: 'Load Board',
      route: 'load-board',
      matchPath: '/app/load-board/:id*',
      treatmentKeys: ['app-route-load-board'],
    },
    {
      icon: <Warehouse />,
      title: 'Assets',
      route: 'assets',
      matchPath: '/app/assets/:id*',
      treatmentKeys: ['app-route-assets'],
    },
    {
      icon: <MonetizationOn />,
      title: 'Ledger',
      route: 'ledger',
      matchPath: '/app/ledger/:id*',
      treatmentKeys: ['app-route-ledger'],
    },
    {
      icon: <Summarize />,
      title: 'Reports',
      route: 'reports',
      matchPath: '/app/reports/:id*',
      treatmentKeys: ['app-route-reports'],
    },
  ]

  if (!isReady) return []

  return routes.filter((item: AppTabRoute) => {
    // if there are any feature flags, we check if they are all on
    const routeTreatmentIsOn = (item?.treatmentKeys || []).every(
      treatmentKey => treatments[treatmentKey]?.treatment === 'on',
    )
    // only enforcing role based access if it has been saved in settings for yard
    // https://greenspark-software.atlassian.net/browse/GREEN-10873
    let userRoleHasAccess = true
    if (rbacMap?.[item.route] == false) {
      userRoleHasAccess = rbacMap[item.route]
    }

    // validation extra for ledger tab
    if (item?.route === 'ledger' && routeTreatmentIsOn && userRoleHasAccess) {
      return !!erpType
    }

    return routeTreatmentIsOn && userRoleHasAccess
  })
}

export const INVENTORY_TAB_ROUTES = [
  {
    title: 'Inventory Overview',
    route: 'commodities',
    matchPath: '/app/inventory/commodities/:id*',
  },
  {
    title: 'Regrades',
    route: 'regrades',
    matchPath: '/app/inventory/regrades',
  },
  {
    title: 'Finished Goods',
    route: 'finished-goods',
    matchPath: '/app/inventory/finished-goods',
  },
  {
    title: 'Packing List',
    route: 'packing-lists',
    matchPath: '/app/inventory/packing-lists',
  },
  {
    title: 'Adjustments',
    route: 'adjustments',
    matchPath: '/app/inventory/adjustments',
  },
  {
    title: 'Negative Inventory',
    route: 'negative-inventory',
    matchPath: '/app/inventory/negative-inventory',
  },
]

export const ACCOUNT_TAB_ROUTES = [
  {
    title: 'Profile',
    route: 'profile',
    matchPath: '/app/account/profile',
  },
  // {
  //   title: 'Security / Login',
  //   route: 'security',
  //   matchPath: '/app/account/security',
  // },
]

export const ADMIN_TAB_ROUTES = [
  {
    icon: <Home />,
    title: 'Orgs',
    route: 'organizations',
    matchPath: '/admin/organizations/:id*',
  },
  {
    icon: <People />,
    title: 'Users',
    route: 'users',
    matchPath: '/admin/users/:id*',
  },
  {
    icon: <Favorite />,
    title: 'Health Monitor',
    route: 'monitor',
    matchPath: '/admin/monitor',
  },
]

export const PURCHASES_TAB_ROUTES: AppTab[] = [
  {
    title: 'Purchase Tickets',
    route: 'ticket-library',
    matchPath: '/app/purchases/ticket-library/:id*',
  },
  {
    title: 'Bills',
    route: 'bills',
    matchPath: '/app/purchases/bills/:id*',
  },
  {
    title: 'Batch Bills',
    route: 'batch-bills',
    matchPath: '/app/purchases/batch-bills/:id*',
  },
]

export const SALES_TAB_ROUTES: AppTab[] = [
  {
    title: 'Outbound Tickets',
    route: 'outbound-tickets',
    matchPath: '/app/sales/outbound-tickets/:id*',
  },
  {
    title: 'Invoices',
    route: 'invoices',
    matchPath: '/app/sales/invoices/:id*',
  },
  {
    title: 'Bulk Invoice Payments',
    route: 'invoice-payments',
    matchPath: '/app/sales/invoice-payments/:id*',
  },
]

export const CONTRACT_TAB_ROUTES = [
  {
    title: 'Sales Orders',
    route: 'sales-orders',
    matchPath: '/app/contracts/sales-orders/:id*',
  },
  {
    title: 'Purchase Orders',
    route: 'purchase-orders',
    matchPath: '/app/contracts/purchase-orders/:id*',
  },
]

export const CUSTOMER_TAB_ROUTES = [
  {
    title: 'Account',
    route: 'account',
    matchPath: '/app/customers/:id/account',
  },
  {
    title: 'Ticket History',
    route: 'tickets',
    matchPath: '/app/customers/:id/tickets',
  },
  {
    title: 'Contract History',
    route: 'contracts',
    matchPath: '/app/customers/:id/contracts',
  },
  {
    title: 'Locations',
    route: 'locations',
    matchPath: '/app/customers/:id/locations',
  },
  {
    title: 'Vehicles',
    route: 'vehicles',
    matchPath: '/app/customers/:id/vehicles',
  },
  {
    title: 'Contacts',
    route: 'contacts',
    matchPath: '/app/customers/:id/contacts',
  },
  {
    title: 'Financial Information',
    route: 'financial-information',
    matchPath: '/app/customers/:id/financial-information',
  },
]

export const FREIGHT_TAB_ROUTES = [
  {
    title: 'Freight Lanes',
    route: 'freight-lanes',
    matchPath: '/app/freight/freight-lanes',
  },
  {
    title: 'Freight Bills',
    route: 'freight-bills',
    matchPath: '/app/freight/freight-bills/:id*',
  },
]

export const ANALYTICS_TAB_ROUTES = [
  {
    title: 'Inbound Ticket Summary',
    route: 'inbound-ticket-summary',
    matchPath: '/app/analytics/inbound-ticket-summary',
  },
  {
    title: 'Outbound Ticket Summary',
    route: 'outbound-ticket-summary',
    matchPath: '/app/analytics/outbound-ticket-summary',
  },
]

export const REPORTS_TAB_ROUTES = [
  {
    title: 'Standard Reports',
    route: 'standard',
    matchPath: '/app/reports/standard/:id*',
  },
  {
    title: 'Custom Reports',
    route: 'custom',
    matchPath: '/app/reports/custom/:id*',
  },
]

interface AdminRoute {
  icon: ReactNode
  title: string
  route: string
  matchPath: string
  permittedRoles?: GS_ROLE[]
  treatmentKey?: SplitFeatureFlagName
}

export const SETTINGS_ADMIN_ROUTES: AdminRoute[] = [
  {
    icon: <Dashboard />,
    title: 'Overview',
    route: 'settings/overview',
    matchPath: '/app/settings/overview/:id*',
  },
  {
    icon: <People />,
    title: 'Users',
    route: 'settings/users',
    matchPath: '/app/settings/users/:id*',
    permittedRoles: [GS_ROLE.YARD_ADMIN],
  },
  {
    icon: <ManageAccounts />,
    title: 'Roles & Permissions',
    route: '/app/settings/roles-and-permissions',
    matchPath: '/app/settings/roles-and-permissions/:id*',
    permittedRoles: [GS_ROLE.YARD_ADMIN],
  },
  {
    icon: <Description />,
    title: 'Documents',
    route: 'settings/documents',
    matchPath: '/app/settings/documents/:id*',
  },
  // {
  //   icon: <Payment />,
  //   title: 'Billing',
  //   route: 'settings/billing',
  //   matchPath: '/app/settings/billing/:id*',
  // },
  // {
  //   icon: <MergeType />,
  //   title: 'Integrations',
  //   route: 'settings/integrations',
  //   matchPath: '/app/settings/integrations/:id*',
  // },
]

export const SETTINGS_PRODUCT_ROUTES = [
  {
    icon: <LocalOffer />,
    title: 'Purchases',
    route: 'settings/purchases',
    matchPath: '/app/settings/purchases/:id*',
  },
  // {
  //   icon: <AssignmentTurnedIn />,
  //   title: 'Sales',
  //   route: 'settings/sales',
  //   matchPath: '/app/settings/sales/:id*',
  // },
  {
    icon: <FileCopy />,
    title: 'Contracts',
    route: 'settings/contracts',
    matchPath: '/app/settings/contracts/:id*',
  },
  {
    icon: <List />,
    title: 'Inventory',
    route: 'settings/inventory',
    matchPath: '/app/settings/inventory/:id*',
  },
  {
    icon: <SupervisedUserCircle />,
    title: 'Customers',
    route: 'settings/customers',
    matchPath: '/app/settings/customers/:id*',
  },
  // {
  //   icon: <PieChart />,
  //   title: 'Commodities',
  //   route: 'settings/commodities',
  //   matchPath: '/app/settings/commodities/:id*',
  // },
  // {
  //   icon: <BarChart />,
  //   title: 'Reports',
  //   route: 'settings/reports',
  //   matchPath: '/app/settings/reports/:id*',
  // },
  {
    icon: <LocalShipping />,
    title: 'Dispatch',
    route: 'settings/dispatch',
    treatmentKey: 'app-route-load-board',
    matchPath: '/app/settings/dispatch/:id*',
  },
  {
    icon: <AttachMoney />,
    title: 'Ledger',
    route: 'settings/ledger',
    matchPath: '/app/settings/ledger/:id*',
  },
  // {
  //   icon: <Settings />,
  //   title: 'Admin',
  //   route: 'settings/admin',
  //   matchPath: '/app/settings/admin/:id*',
  // },
]

export const SETTINGS_BILLING_TAB_ROUTES = [
  {
    title: 'Test',
    route: 'test',
    matchPath: '/app/settings/billing/test',
  },
  {
    title: 'Test2',
    route: 'test2',
    matchPath: '/app/settings/billing/test2',
  },
  {
    title: 'Test3',
    route: 'test3',
    matchPath: '/app/settings/billing/test3',
  },
]

export const SETTINGS_INTEGRATIONS_TAB_ROUTES = [
  {
    title: 'Test',
    route: 'test',
    matchPath: '/app/settings/integrations/test',
  },
  {
    title: 'Test2',
    route: 'test2',
    matchPath: '/app/settings/integrations/test2',
  },
  {
    title: 'Test3',
    route: 'test3',
    matchPath: '/app/settings/integrations/test3',
  },
]

export const SETTINGS_PURCHASES_TAB_ROUTES = [
  {
    title: 'Inbound Tickets',
    route: 'inbound-tickets',
    matchPath: '/app/settings/purchases/inbound-tickets',
  },
  {
    title: 'Cash Drawers',
    route: 'cash-drawers',
    matchPath: '/app/settings/purchases/cash-drawers',
  },
  {
    title: 'Adjustments',
    route: 'adjustments',
    matchPath: '/app/settings/purchases/adjustments',
  },
  {
    title: 'Scale View',
    route: 'scale-view',
    matchPath: '/app/settings/purchases/scale-view',
  },
  // {
  //   title: 'Quick Access Materials',
  //   route: 'quick-access-materials',
  //   matchPath: '/app/settings/purchases/quick-access-materials',
  // },
  // {
  //   title: 'Ticket Approvals',
  //   route: 'ticket-approvals',
  //   matchPath: '/app/settings/purchases/ticket-approvals',
  // },
  {
    title: 'Payment',
    route: 'payment',
    matchPath: '/app/settings/purchases/payment',
  },
  // {
  //   title: 'Other Settings',
  //   route: 'other-settings',
  //   matchPath: '/app/settings/purchases/other-settings',
  // },
  {
    title: 'Accounts',
    route: 'accounts',
    matchPath: '/app/settings/purchases/accounts',
  },
]

export const SETTINGS_SALES_TAB_ROUTES = [
  {
    title: 'Test',
    route: 'test',
    matchPath: '/app/settings/sales/test',
  },
  {
    title: 'Test2',
    route: 'test2',
    matchPath: '/app/settings/sales/test2',
  },
  {
    title: 'Test3',
    route: 'test3',
    matchPath: '/app/settings/sales/test3',
  },
]

export const SETTINGS_CONTRACT_TAB_ROUTES = [
  {
    title: 'General',
    route: 'contracts-general',
    matchPath: '/app/settings/contracts/contracts-general',
    treatmentKey: 'GREEN-12347-close-contracts',
  },
  {
    title: 'Purchase Orders',
    route: 'purchase-orders',
    matchPath: '/app/settings/contracts/purchase-orders',
  },
  {
    title: 'Sales Orders',
    route: 'sales-orders',
    matchPath: '/app/settings/contracts/sales-orders',
  },
  {
    title: 'Payment Terms',
    route: 'payment-terms',
    matchPath: '/app/settings/contracts/payment-terms',
  },
  // {
  //   title: 'Approvals',
  //   route: 'approvals',
  //   matchPath: '/app/settings/contracts/approvals',
  // },
  // {
  //   title: 'F.O.B. Terms',
  //   route: 'fob-terms',
  //   matchPath: '/app/settings/contracts/fob-terms',
  // },
]

export const SETTINGS_INVENTORY_TAB_ROUTES = [
  {
    title: 'General',
    route: 'general',
    matchPath: '/app/settings/inventory/general',
  },
  // {
  //   title: 'Outbound Tickets',
  //   route: 'outbound-tickets',
  //   matchPath: '/app/settings/inventory/outbound-tickets',
  // },
  {
    title: 'Finished Goods',
    route: 'finished-goods',
    matchPath: '/app/settings/inventory/finished-goods',
  },
]
export const SETTINGS_CUSTOMERS_TAB_ROUTES = [
  {
    title: 'Supplier Groupings',
    route: 'supplier-groupings',
    matchPath: '/app/settings/customers/supplier-groupings',
  },
  {
    title: 'Compliance',
    route: 'compliance',
    matchPath: '/app/settings/customers/compliance',
  },
]
export const SETTINGS_COMMODITIES_TAB_ROUTES = [
  {
    title: 'Test',
    route: 'test',
    matchPath: '/app/settings/commodities/test',
  },
  {
    title: 'Test2',
    route: 'test2',
    matchPath: '/app/settings/commodities/test2',
  },
  {
    title: 'Test3',
    route: 'test3',
    matchPath: '/app/settings/commodities/test3',
  },
]
export const SETTINGS_REPORTS_TAB_ROUTES = [
  {
    title: 'Test',
    route: 'test',
    matchPath: '/app/settings/reports/test',
  },
  {
    title: 'Test2',
    route: 'test2',
    matchPath: '/app/settings/reports/test2',
  },
  {
    title: 'Test3',
    route: 'test3',
    matchPath: '/app/settings/reports/test3',
  },
]
export const SETTINGS_DISPATCH_TAB_ROUTES = [
  {
    title: 'Drivers',
    route: 'drivers',
    matchPath: '/app/settings/dispatch/drivers',
  },
  {
    title: 'Asset Types',
    route: 'asset-types',
    matchPath: '/app/settings/dispatch/asset-types',
  },
  {
    title: 'Load Board',
    route: 'load-board',
    matchPath: '/app/settings/dispatch/load-board',
    treatmentKey: 'GREEN-9501-roll-over-load-board',
  },
]

export const SETTINGS_ADMIN_TAB_ROUTES = [
  {
    title: 'Test',
    route: 'test',
    matchPath: '/app/settings/admin/test',
  },
  {
    title: 'Test2',
    route: 'test2',
    matchPath: '/app/settings/admin/test2',
  },
  {
    title: 'Test3',
    route: 'test3',
    matchPath: '/app/settings/admin/test3',
  },
]

export const LEDGER_TAB_ROUTES = [
  {
    title: 'Sync',
    route: 'sync',
    matchPath: '/app/ledger/sync/:id*',
  },
  {
    title: 'Sync Log',
    route: 'sync-log',
    matchPath: '/app/ledger/sync-log/:id*',
  },
]

export const MATERIAL_PROPERTIES_ROUTES = [
  {
    title: 'Overview',
    route: 'overview',
    matchPath: '/app/commodities/:commodityId/:materialName/:materialId/overview',
  },
  {
    title: 'Pricing',
    route: 'pricing',
    matchPath: '/app/commodities/:commodityId/:materialName/:materialId/pricing',
  },
]

export const COMMODITIES_TAB_ROUTES = [
  {
    title: 'Commodity List',
    route: 'commodity-list',
    matchPath: '/app/commodities/commodity-list/:id*',
  },
  {
    title: 'Price Lists',
    route: 'price-lists',
    matchPath: '/app/commodities/price-lists/:id*',
  },
]

export const LOAD_BOARD_TAB_ROUTES = [
  {
    title: 'Load Board',
    route: 'active-tickets',
    matchPath: '/app/load-board/active-tickets',
  },
  {
    title: 'Dispatch Tickets',
    route: 'ticket-history',
    matchPath: '/app/load-board/ticket-history/:id*',
  },
  // {
  //   title: 'Recurring Tickets',
  //   route: 'recurring-tickets',
  //   matchPath: '/app/load-board/recurring-tickets',
  // },
  // {
  //   title: 'Calendar',
  //   route: 'calendar',
  //   matchPath: '/app/load-board/calendar',
  // },
]

export const ASSET_TAB_ROUTES = [
  {
    title: 'Asset Map',
    route: 'asset-map',
    matchPath: '/app/assets/asset-map',
  },
  {
    title: 'Containers',
    route: 'containers',
    matchPath: '/app/assets/containers',
  },
  {
    title: 'Trucks',
    route: 'trucks',
    matchPath: '/app/assets/trucks',
  },
  {
    title: 'Trailers',
    route: 'trailers',
    matchPath: '/app/assets/trailers',
  },
]

export const ASSET_DETAIL_TAB_ROUTES = [
  {
    title: 'PROFILE',
    route: 'profile',
    matchPath: '/app/assets/:assetCategory/:assetId*/profile',
  },
  {
    title: 'HISTORY',
    route: 'history',
    matchPath: '/app/assets/:assetCategory/:assetId*/history',
  },
]
